import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Upload } from 'lucide-react'
import { useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import { api, errorText } from '../../api.ts'
import type { AppHealth, AppKind, HostApp } from '../../types.ts'
import { APP_ICONS_KEY, AppMark, Badge, Field, Loading, Modal, Notice, when } from '../../ui.tsx'
import { Button, buttonClass } from '../../components/Button.tsx'
import { SegmentedControl } from '../../components/SegmentedControl.tsx'

const ICON_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']

/** Every app and MCP server on this host, ICE included, with what's actually running: read
 *  live from each one's /health (PLAN.md 1.6). Each version links to its GitHub Release. One
 *  that stops answering keeps showing its last answer until the page is reloaded. Icefield
 *  administrators add apps and MCP servers here, and set their icons. */
export function HostApps() {
  const lastGood = useRef(new Map<string, AppHealth>())
  const apps = useQuery({
    queryKey: ['admin', 'apps'],
    queryFn: async () => {
      const { apps } = await api.get<{ apps: HostApp[] }>('/admin/apps')
      for (const a of apps) if (a.health?.reachable) lastGood.current.set(a.appId, a.health)
      return apps
    },
    refetchInterval: 30_000,
  })
  const [editing, setEditing] = useState<HostApp | null>(null)
  const [adding, setAdding] = useState(false)

  return (
    <section>
      <div className="section-head">
        <h1>Apps on this host</h1>
        <div className="section-head-end">
          <span className="muted small">{apps.dataUpdatedAt ? `Checked ${when(new Date(apps.dataUpdatedAt).toISOString())}` : ''}</span>
          <Button icon={Plus} onClick={() => setAdding(true)}>Add app</Button>
        </div>
      </div>
      {apps.isPending ? (
        <Loading />
      ) : apps.isError ? (
        <Notice>{errorText(apps.error)}</Notice>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>App</th><th>Health</th><th>Version</th><th>Commit</th><th>Built</th><th aria-label="Actions" /></tr>
            </thead>
            <tbody>
              {apps.data.map((a) => {
                const live = a.health
                const shown = live?.reachable ? live : lastGood.current.get(a.appId) ?? null
                const ice = a.appId === 'ice' || a.appId === 'ice-worker'
                return (
                  <tr key={a.appId}>
                    <td>
                      <div className="app-cell">
                        {!ice && <AppMark appId={a.appId} name={a.name} kind={a.kind} size={28} />}
                        <div>
                          <strong>{a.name}</strong> {a.kind === 'mcp' && <Badge>MCP server</Badge>}
                          {!ice && <div className="muted small mono">{a.appId}</div>}
                          {a.kind === 'mcp' ? (
                            <>
                              <div className="small mono">{a.mcpUrl}</div>
                              {a.mcpScopes && a.mcpScopes.length > 0 && <div className="muted small">{a.mcpScopes.join(' ')}</div>}
                            </>
                          ) : (
                            a.publicUrl && <div className="small"><a href={a.publicUrl}>{a.publicUrl}</a></div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td><HealthBadge health={live} /></td>
                    <td>
                      {shown?.version ? (
                        a.releaseUrl && live?.reachable ? <a href={a.releaseUrl}>v{shown.version}</a> : `v${shown.version}`
                      ) : '—'}
                      {!live?.reachable && shown && <div className="muted small">last answer</div>}
                    </td>
                    <td className="mono">{shown?.commit ? shown.commit.slice(0, 7) : '—'}</td>
                    <td className="nowrap">{when(shown?.builtAt ?? null)}</td>
                    <td className="actions">
                      {!ice && <Button variant="tertiary" size="small" onClick={() => setEditing(a)}>Edit</Button>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small">Deployment history and test results are in each app's GitHub Releases, so they're there even when ICE isn't.</p>
      <Modal title="Add an app" open={adding} onClose={() => setAdding(false)}>
        {adding && (
          <AppForm
            app={null}
            onDone={(created) => {
              setAdding(false)
              setEditing(created)
            }}
          />
        )}
      </Modal>
      <Modal title={`Edit ${editing?.name ?? ''}`} open={editing !== null} onClose={() => setEditing(null)}>
        {editing && (
          <>
            <IconForm app={editing} onChange={setEditing} />
            <AppForm app={editing} onDone={() => setEditing(null)} />
          </>
        )}
      </Modal>
    </section>
  )
}

function HealthBadge({ health }: { health: AppHealth | null }) {
  if (!health) return <Badge>Not set up</Badge>
  if (!health.reachable) return <span title={health.error ?? ''}><Badge tone="bad">Unreachable</Badge></span>
  if (health.status === 'ok' && !health.error) return <Badge tone="ok">Healthy</Badge>
  return <span title={health.error ?? ''}><Badge tone="warn">{health.status ?? 'Unhealthy'}</Badge></span>
}

/** A new app or MCP server (app null), or an existing one's settings. Its id can't change once
 *  it's made: tokens, sign-in clients and the change feed all use it. */
function AppForm({ app, onDone }: { app: HostApp | null; onDone: (saved: HostApp) => void }) {
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<AppKind>(app?.kind ?? 'app')
  const [appId, setAppId] = useState('')
  const [name, setName] = useState(app?.name ?? '')
  const [publicUrl, setPublicUrl] = useState(app?.publicUrl ?? '')
  const [healthUrl, setHealthUrl] = useState(app?.healthUrl ?? '')
  const [repo, setRepo] = useState(app?.repo ?? '')
  const [mcpUrl, setMcpUrl] = useState(app?.mcpUrl ?? '')
  const [mcpScopes, setMcpScopes] = useState((app?.mcpScopes ?? []).join(' '))
  const [setupNote, setSetupNote] = useState(app?.setupNote ?? '')
  const [adminUrl, setAdminUrl] = useState(app?.adminUrl ?? '')
  const [connectUrl, setConnectUrl] = useState(app?.connectUrl ?? '')
  const isMcp = kind === 'mcp'
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const fields = isMcp
      ? {
          name: name.trim(),
          healthUrl: healthUrl.trim() || null,
          repo: repo.trim() || null,
          mcpUrl: mcpUrl.trim(),
          mcpScopes: mcpScopes.split(/\s+/).filter(Boolean),
          setupNote: setupNote.trim() || null,
          adminUrl: adminUrl.trim() || null,
          connectUrl: connectUrl.trim() || null,
        }
      : { name: name.trim(), publicUrl: publicUrl.trim() || null, healthUrl: healthUrl.trim() || null, repo: repo.trim() || null }
    try {
      const saved = app
        ? await api.patch<HostApp>(`/admin/apps/${app.appId}`, fields)
        : await api.post<HostApp>('/admin/apps', { id: appId.trim(), kind, ...fields })
      await queryClient.invalidateQueries({ queryKey: ['admin', 'apps'] })
      onDone(saved)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <Notice>{error}</Notice>
      {!app && (
        <>
          <div className="field">
            <span className="field-label">Kind</span>
            <SegmentedControl
              label="Kind"
              value={kind}
              onChange={setKind}
              options={[{ value: 'app', label: 'App' }, { value: 'mcp', label: 'MCP server' }]}
            />
          </div>
          <Field label="Id" hint="Lower-case letters, digits and hyphens. Its token, sign-in and data in ICE all use it, so it can't be changed later.">
            <input required value={appId} onChange={(e) => setAppId(e.target.value)} pattern="[a-z][a-z0-9\-]*" maxLength={60} placeholder="lead-rescue" />
          </Field>
        </>
      )}
      <Field label="Name" hint="As people see it on the dashboard.">
        <input required value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
      </Field>
      {isMcp ? (
        <>
          <Field
            label="MCP address"
            hint="What people paste into Claude, and the audience of its access tokens. {company} stands for the company's slug, for one address per company."
          >
            <input required value={mcpUrl} onChange={(e) => setMcpUrl(e.target.value)} placeholder="https://" />
          </Field>
          <Field label="Scopes" hint="The server's own, separated by spaces. Claude gets those it asks for.">
            <input value={mcpScopes} onChange={(e) => setMcpScopes(e.target.value)} placeholder="itm:read itm:write" />
          </Field>
          <Field label="Setup note" hint="Shown on its setup page, above the steps. Optional.">
            <input value={setupNote} onChange={(e) => setSetupNote(e.target.value)} maxLength={500} />
          </Field>
          <Field
            label="Admin setup page"
            hint="Optional. Where a company's administrator sets it up for their company, on its own site. Only company administrators see the link."
          >
            <input type="url" value={adminUrl} onChange={(e) => setAdminUrl(e.target.value)} placeholder="https://" />
          </Field>
          <Field
            label="Connect page"
            hint="Optional. Where each person links their own account, on its own site. Everyone who has it sees the link, after the steps for adding it to Claude."
          >
            <input type="url" value={connectUrl} onChange={(e) => setConnectUrl(e.target.value)} placeholder="https://" />
          </Field>
        </>
      ) : (
        <Field label="Public address" hint="Where its dashboard tile goes.">
          <input type="url" value={publicUrl} onChange={(e) => setPublicUrl(e.target.value)} placeholder="https://" />
        </Field>
      )}
      <Field label="Health address on this host" hint="Read by ICE over the host, as in http://127.0.0.1:3022/health.">
        <input type="url" value={healthUrl} onChange={(e) => setHealthUrl(e.target.value)} />
      </Field>
      <Field label="GitHub repository" hint="owner/name. Each version links to its release there.">
        <input value={repo} onChange={(e) => setRepo(e.target.value)} placeholder="IcefieldAI/timekeeping" />
      </Field>
      {!app && (
        <p className="muted small">
          No company has it until you switch it on for one. If it reads ICE's API or signs people in, make its token and sign-in client on the
          server: <code>npm run token:create -- {appId.trim() || 'its-id'}</code> and <code>npm run client:set</code>.
        </p>
      )}
      <div className="form-actions">
        <Button type="submit" disabled={busy}>{app ? 'Save' : 'Add'}</Button>
      </div>
    </form>
  )
}

/** An app's icon: uploaded, replaced or removed at once, as a company's logo is. */
function IconForm({ app, onChange }: { app: HostApp; onChange: (app: HostApp) => void }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const run = async (work: () => Promise<HostApp>) => {
    setBusy(true)
    setError(null)
    try {
      onChange(await work())
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: APP_ICONS_KEY }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'apps'] }),
      ])
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const upload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!ICON_TYPES.includes(file.type)) return setError('Use a PNG, JPEG, WebP or SVG icon')
    if (file.size > 512 * 1024) return setError('Keep the icon under 512 KB')
    void run(() => api.put<HostApp>(`/admin/apps/${app.appId}/icon`, file, file.type))
  }

  return (
    <div className="form">
      <Notice>{error}</Notice>
      <div className="logo-row">
        <AppMark appId={app.appId} name={app.name} kind={app.kind} size={72} />
        <div className="logo-actions">
          <label className={buttonClass({ variant: 'secondary' })}>
            <Upload aria-hidden="true" />
            {app.iconUrl ? 'Replace icon' : 'Upload icon'}
            <input type="file" accept={ICON_TYPES.join(',')} hidden disabled={busy} onChange={upload} data-autosave />
          </label>
          {app.iconUrl && (
            <Button variant="destructive-tertiary" icon={Trash2} disabled={busy} onClick={() => run(() => api.delete<HostApp>(`/admin/apps/${app.appId}/icon`))}>
              Remove
            </Button>
          )}
        </div>
      </div>
      <p className="muted small">
        {app.kind === 'mcp'
          ? "The logo of the product it connects to, shown with a connector badge on the dashboard and when Claude signs in. "
          : 'Its tile: square, 256 by 256 or larger, shown wherever the dashboard shows the app. '}
        PNG, JPEG, WebP or SVG, up to 512 KB.
      </p>
    </div>
  )
}
